<template>
  <div>
    <form-field-select
      @change="changeStatus"
      v-model="activeStatusId"
      :help-text="hasActiveFollowUp ? 'Non follow up statuses are disabled while in an automated follow up' : ''"
    >
      <template #before>
        <div class="control">
          <p class="button is-static is-small">{{ label }}</p>
        </div>
      </template>

      <optgroup label="Contact Not Made">
        <option
          v-for="status in statusesForContacting"
          :key="status.id"
          :value="status.id"
          :disabled="hasActiveFollowUp"
        >{{ status.label }}
        </option>
      </optgroup>

      <optgroup label="Active">
        <option
          v-for="status in statusesForActive"
          :key="status.id"
          :value="status.id"
          :disabled="hasActiveFollowUp"
        >{{ status.label }}
        </option>
      </optgroup>

      <optgroup label="Follow Up">
        <option
          v-for="status in statusesForFollowUp"
          :key="status.id"
          :value="status.id"
        >{{ status.label }}
        </option>
      </optgroup>

      <optgroup label="Completed">
        <option
          v-for="status in statusesForCompleted"
          :key="status.id"
          :value="status.id"
          :disabled="hasActiveFollowUp"
        >{{ status.label }}
        </option>
      </optgroup>

      <optgroup label="Dead">
        <option
          v-for="status in statusesForDead"
          :key="status.id"
          :value="status.id"
          :disabled="hasActiveFollowUp"
        >{{ status.label }}
        </option>
      </optgroup>

      <optgroup label="Non Lead">
        <option
          v-for="status in statusesForNonLead"
          :key="status.id"
          :value="status.id"
          :disabled="hasActiveFollowUp"
        >{{ status.label }}
        </option>
      </optgroup>
    </form-field-select>
  </div>
</template>

<script>
import {ENUMS} from "../../services/enums";
import {appendModal} from "../../services/modals";
import PromptDateModal from "../modal/PromptDateModal";
import PromptNextContactModal from "../modal/PromptNextContactModal";

export default {
  name: "RecordStatusSelector",
  components: {PromptDateModal},
  props: {
    record: {
      type: Object,
      required: true
    },
    label: {
      type: String,
      default: "Status"
    },
  },
  data() {
    return {
      activeStatusId: null,
      oldActiveStatusId: null,
    };
  },
  computed: {
    oldActiveStatus() {
      return this.allStatuses.find(status => status.id === this.oldActiveStatusId);
    },
    hasActiveFollowUp() {
      return false
      // return !!this.record.active_sequence_id;
    },
    allStatuses() {
      return this.$store.getters["pipeline/statuses"];
    },
    statusesForContacting() {
      return this.$store.getters["pipeline/contactingStatuses"];
    },
    statusesForActive() {
      return this.$store.getters["pipeline/activeStatuses"];
    },
    statusesForFollowUp() {
      return this.$store.getters["pipeline/followUpStatuses"];
    },
    statusesForCompleted() {
      return this.$store.getters["pipeline/completedStatuses"];
    },
    statusesForDead() {
      return this.$store.getters["pipeline/deadStatuses"];
    },
    statusesForNonLead() {
      return this.$store.getters["pipeline/nonLeadStatuses"];
    },
  },
  created() {
    this.activeStatusId = this.record.current_status_id;
    this.oldActiveStatusId = this.record.current_status_id;
  },
  methods: {
    undoActiveStatusChange() {
      this.activeStatusId = this.oldActiveStatusId;
    },
    changeStatus(newStatusId) {
      const newStatus = this.$store.getters["pipeline/statuses"].find(status => status.id === newStatusId);

      // prompt for next contact if required
      if (this.nextContactAtPromptRequired(newStatus)) {
        let $prompt = appendModal(PromptNextContactModal, this, {record: this.record});

        $prompt.$on("canceled", this.undoActiveStatusChange);
        $prompt.$on("confirmed", (nextContactAt) => {
          this.changeStatusAPICall(newStatus.id, null, nextContactAt);
        });
      }
      // prompt for due date if required
      // else if (this.dueDatePromptRequired(newStatus)) {
      //   let $prompt = appendModal(PromptDateModal, this);
      //
      //   $prompt.$on("canceled", this.undoActiveStatusChange);
      //   $prompt.$on("confirmed", (dueAt) => {
      //     this.changeStatusAPICall(newStatus.id, dueAt);
      //   });
      // }
      // change status with no prompts
      else {
        this.changeStatusAPICall(newStatus.id);
      }
    },
    nextContactAtPromptRequired(newStatus) {
      const oldState = this.oldActiveStatus.state;
      const newState = newStatus.state;
      const currentNextContactAt = this.record.next_contact_at;

      const contacting = ENUMS.StatusStateEnum.Contacting.value;
      const followUp = ENUMS.StatusStateEnum.FollowUp.value;

      // This is here because next_contact_at is required in these states
      if (currentNextContactAt === null && (newState === contacting || newState === followUp)) return true;

      // No prompt if changing statuses within the same state
      if (oldState === contacting && newState === contacting) return false;
      if (oldState === followUp && newState === followUp) return false;

      // Finally, only if new state is contacting or follow up then prompt
      return newState === contacting || newState === followUp;
    },
    dueDatePromptRequired(newStatus) {
      const active = ENUMS.StatusStateEnum.Active.value;

      return newStatus.state === active && newStatus.acceptable_time === null;
    },
    changeStatusAPICall(newStatusId, dueAt = null, nextContactAt = null) {
      this.$api.records.changeStatus(this.record.id, newStatusId, dueAt, nextContactAt)
        .then((res) => {
          this.oldActiveStatusId = this.activeStatusId;

          this.$buefy.toast.open({
            message: "Lead status has been updated",
            type: "is-success"
          });
        })
        .catch(() => {
          this.undoActiveStatusChange();
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";


.button.is-static {
  line-height: 2.5em; // it doesn't vertically center without this
  background-color: $primary;
  border-color: transparent;
  color: $white;
}

::v-deep .select select,
::v-deep .input {
  border-color: $primary;
  color: $primary;
}

::v-deep .input {
  cursor: default;
}
</style>
